/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import {
  ComponentManager,
  ComponentManagerFn,
  ViewAttributeChooserProp,
} from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { baseFieldProps } from '../TextField/BaseField';
import { defaultValue } from './InputBase';

const InputBaseManager: ComponentManagerFn<'InputBase'> = (
  platform: Platform,
  type: 'InputBase',
): ComponentManager<'InputBase'> => {
  return {
    ...baseComponentManager<'InputBase'>(platform, 'InputBase'),
    type: 'InputBase',
    isChildAllowed: (page, item, childType) =>
      (['TriggerOnChange'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      ...baseFieldProps,
      viewAttribute: {
        ...(baseFieldProps.viewAttribute as ViewAttributeChooserProp),
        getAttributeDataTypes: () => {
          return [
            'Email',
            'EncryptedString',
            'Phone',
            'String',
            'Textarea',
            'URL',
            'UserID',
          ];
        },
      },
      placeholder: {
        label: 'Placeholder',
        type: 'string',
      },
      onChangeFunction: {
        label: 'On Change',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      onKeyDownFunction: {
        label: 'On Key Down',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      onFocusFunction: {
        label: 'On Focus',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      onBlurFunction: {
        label: 'On Blur',
        type: 'controllerFunction',
        category: 'Controllers',
      },
      autoFocus: {
        label: 'Auto Focus',
        type: 'boolean',
        helpText: 'Check this to focus the input field when the page is loaded',
      },
    },
    name: 'Input Base',
    category: 'Forms',
    isPropertyVisible: (property) => {
      switch (property) {
        case 'label':
        case 'colorExpression':
        case 'bgColorExpression':
        case 'flex':
        case 'helperText': {
          return false;
        }
        default:
          return true;
      }
    },
    icon: { iconName: 'io-text-input', iconType: 'cloudio' },
    description: `**Overview**<br/>
The Input Base component is a stripped-down, lightweight text entry field designed for building custom UI elements. Unlike the standard **TextField**, this component intentionally hides layout properties like **Labels**, **Helper Text**, and **Background Colors**.
Use this component when you need a raw input box without the standard form structure—typically when building search bars, custom widgets, or embedding inputs inside headers and toolbars where space is limited.

**1. Data Binding**<br/>
This component binds to various text-based fields in your Data Store.
* **View Attribute:** Select the Data Store field to read from and write to.
* **Supported Types:** String, Email, Phone, URL, Textarea, EncryptedString, UserID.

**2. Minimalist Configuration**<br/>
Because this component is designed for custom layouts, standard form properties are disabled. It focuses purely on the input interaction.
* **Placeholder:** Ghost text displayed when the field is empty (e.g., "Search...").
* **Auto Focus:** A boolean toggle. If enabled, the browser automatically focuses this field when the page loads, allowing the user to type immediately.
* **Hidden Properties:** Note that properties like **Label**, **Color Expression**, and **Helper Text** are explicitly hidden to keep the component "clean."

**3. Events & Controllers**<br/>
The Input Base provides deep access to user interaction events, making it ideal for programmed logic.
* **Triggers:** Supports **TriggerOnChange** for low-code logic execution.
* **Controller Functions:** Binds directly to JavaScript/TypeScript functions for advanced handling:
    * **onKeyDownFunction:** Essential for Search bars (e.g., detecting when the user hits "Enter").
    * **onChangeFunction:** Executes on every keystroke.
    * **onFocusFunction / onBlurFunction:** Handle state changes when the user enters or leaves the field.

**4. Best Use Cases**<br/>
* **Global Search Bars:** Placing a simple input box in the top navigation header without a label.
* **Custom Composite Components:** Building a complex widget (like a "Tag Editor") where the input is just one small part of the design.
* **Data Grids:** Embedding a filter input inside a table header cell.
* **Chat Interfaces:** Creating a message entry box at the bottom of a screen.`,
  };
};

export default InputBaseManager;
